import React, { useMemo, useState } from "react"
import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { format } from "date-fns"
import XLSX from "xlsx"
import {
    useGetAllExperienceCategoriesQuery,
    useLazyGetExperienceSummaryQuery,
} from "../../../core/api/experience-api"

// Export to .xlsx — opens straight as a spreadsheet, no import step.
// `columns` defines column order and headers (by headerName).
function exportToXlsx(filename, columns, rows) {
    const data = rows.map((row) => {
        const out = {}
        for (const c of columns) {
            out[c.headerName] = row[c.field] == null ? "" : row[c.field]
        }
        return out
    })
    const ws = XLSX.utils.json_to_sheet(data, { header: columns.map((c) => c.headerName) })
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "XP")
    XLSX.writeFile(wb, `${filename}.xlsx`)
}

export default function ExperiencePeriodSummary() {
    const { data: xpCategories = [] } = useGetAllExperienceCategoriesQuery()
    const [trigger, { data: summary = [], isFetching, isUninitialized }] = useLazyGetExperienceSummaryQuery()

    // Default range — from the start of the current year to today.
    const [dateFrom, setDateFrom] = useState(new Date(new Date().getFullYear(), 0, 1))
    const [dateTo, setDateTo] = useState(new Date())

    const handleLoad = () => {
        trigger({
            dateFrom: format(dateFrom, "dd.MM.yyyy"),
            dateTo: format(dateTo, "dd.MM.yyyy"),
        })
    }

    // Category keys — the union of the XP category dictionary and all keys
    // actually present in the data (incl. the coupon bucket for uncategorized XP),
    // otherwise their XP would count into totalXp but get no column.
    const categoryKeys = useMemo(() => {
        const keys = new Set(xpCategories.map((c) => c.name))
        for (const r of summary) {
            if (r.categoryXp) Object.keys(r.categoryXp).forEach((k) => keys.add(k))
        }
        return [...keys]
    }, [xpCategories, summary])

    // One column per XP category, value from categoryXp[name] (same as the balances page).
    const columns = useMemo(() => {
        const categoryColumns = categoryKeys.map((name) => ({
            field: `cat_${name}`,
            headerName: name,
            width: 120,
            type: "number",
            valueGetter: (params) => (params.row.categoryXp && params.row.categoryXp[name]) || 0,
        }))
        return [
            { field: "email", headerName: "Email", width: 280 },
            { field: "totalXp", headerName: "XP for period", width: 130, type: "number" },
            ...categoryColumns,
        ]
    }, [categoryKeys])

    const rows = useMemo(
        () => summary.map((r) => {
            // Flatten categoryXp into flat cat_<name> fields — also needed for the CSV export.
            const flat = {}
            for (const name of categoryKeys) {
                flat[`cat_${name}`] = (r.categoryXp && r.categoryXp[name]) || 0
            }
            return { id: r.email, ...flat, ...r }
        }),
        [summary, categoryKeys]
    )

    const handleExport = () => {
        const filename = `xp-summary_${format(dateFrom, "dd.MM.yyyy")}_${format(dateTo, "dd.MM.yyyy")}`
        exportToXlsx(filename, columns, rows)
    }

    return (
        <Box sx={{ py: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>XP for period</Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2, rowGap: 2 }}>
                    <DatePicker
                        label="Date from"
                        value={dateFrom}
                        onChange={(v) => setDateFrom(v)}
                        inputFormat="dd.MM.yyyy"
                        mask="__.__.____"
                        renderInput={(params) => <TextField {...params} />}
                    />
                    <DatePicker
                        label="Date to"
                        value={dateTo}
                        onChange={(v) => setDateTo(v)}
                        inputFormat="dd.MM.yyyy"
                        mask="__.__.____"
                        renderInput={(params) => <TextField {...params} />}
                    />
                    <Button variant="contained" onClick={handleLoad} disabled={!dateFrom || !dateTo || isFetching}>
                        Load
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleExport}
                        disabled={isUninitialized || isFetching || rows.length === 0}
                    >
                        Export to file
                    </Button>
                </Stack>
            </LocalizationProvider>

            {isFetching ? (
                <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>
            ) : (
                <Paper sx={{ height: 640 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSize={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        disableRowSelectionOnClick
                    />
                </Paper>
            )}
        </Box>
    )
}
